import React, { useState, useEffect, useRef } from "react";

import "./stylesBetTypes.css";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { useNavigate, useSearchParams } from "react-router-dom";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";

ChartJS.register(ArcElement, Tooltip, Legend);

const BetTypeDictionary = {
  Straight: "single",
  "Round Robin": "round_robin_leg",
  Parlay: "parlay",
  Group: "grouped_leg",
};

const colors = ["#3498DB", "#6177E8", "#9B59B6", "#E74C3C"];

const options = {
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      display: false,
    },
    tooltip: {
      enabled: true,
      backgroundColor: "#252729",
      titleFont: {
        family: "Poppins-regular",
        size: 16,
        weight: "bold",
      },
      bodyFont: {
        family: "Poppins-regular",
        size: 14,
      },
      padding: 15,
      displayColors: true,
      callbacks: {
        label: function (tooltipItem) {
          const label = tooltipItem.dataset.label || "";
          const value = tooltipItem.raw;
          return `${label}: ${Intl.NumberFormat("en-US").format(value)}`;
        },
      },
    },
  },
};

function BetTypes() {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken,
    false
  );
  const chartRef = useRef();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event-id");

  const [isApiRunning, setIsApiRunning] = useState(false);

  const [chartData, setChartData] = useState({
    labels: ["Straight", "Parlay", "Round Robin", "Group"],
    datasets: [
      {
        label: false,
        data: [0, 0, 0, 0],
        backgroundColor: ["#3498DB", "#6177E8", "#9B59B6", "#E74C3C"],
        borderColor: ["#3498DB", "#6177E8", "#9B59B6", "#E74C3C"],
        borderWidth: 1,
      },
    ],
  });

  const labels = chartData?.labels;

  const handleChartClick = (event) => {
    const chart = chartRef.current;

    if (!chart) return;

    const elements = chart.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      false
    );

    if (elements.length > 0) {
      const { index } = elements[0];
      const label = chartData.labels[index];

      const betType = BetTypeDictionary[label] || 1;
      navigate(`/bets-types-list?bet-type=${betType}&event-id=${eventId}`);
    }
  };

  const getBetTypes = async () => {
    setIsApiRunning(true);
    try {
      const requests = [
        PrivateAdminPanelInstance.post(
          API_ROUTES.GET_EVENT_DETAILS_KPI_BET_TYPES,
          { event_id: eventId, provider: "sportradar" }
        ),
      ];

      const responses = await Promise.allSettled(requests);
      const [sportradarResponse] = responses;

      if (sportradarResponse.status === "fulfilled") {
        const data = sportradarResponse?.value?.data?.data?.data || [];

        const straight =
          data.find((item) => item.label === "Straight")?.value || 0;
        const parlay = data.find((item) => item.label === "Parlay")?.value || 0;
        const roundRobin =
          data.find((item) => item.label === "Round Robin")?.value || 0;
        const group = data.find((item) => item.label === "Group")?.value || 0;

        setChartData({
          labels: ["Straight", "Parlay", "Round Robin", "Group"],
          datasets: [
            {
              label: false,
              data: [straight, parlay, roundRobin, group],
              backgroundColor: ["#3498DB", "#6177E8", "#9B59B6", "#E74C3C"],
              borderColor: ["#3498DB", "#6177E8", "#9B59B6", "#E74C3C"],
              borderWidth: 1,
            },
          ],
        });
      }
    } catch (error) {
      console.log("Error getting bet types:", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  useEffect(() => {
    getBetTypes();
  }, []);

  return (
    <div className="bet-types-container">
      <div className="bet-types-header">
        <h2>Bet Types</h2>
      </div>
      <div className="bet-types-content">
        {isApiRunning ? (
          <RebetLoader width={100} height={100} />
        ) : (
          <>
            <div className="bet-types-chart">
              <Pie
                ref={chartRef}
                data={chartData}
                options={options}
                onClick={handleChartClick}
              />
            </div>
            <div className="bet-types-legend">
              {labels.map((label, index) => (
                <div className="bet-types-legend-item" key={index}>
                  <div
                    className="bet-types-legend-item-color"
                    style={{ backgroundColor: colors[index] }}
                  />
                  <span className="bet-types-legend-item-label">{label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BetTypes;
